import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import * as api from "./api";

// Igual patrón que accessLogQueries.ts/usersQueries.ts: acumulación por
// `useInfiniteQuery` + `CursorTable` con `onLoadMore` disparando
// `fetchNextPage`, no el índice prev/next de MembersScreen.
export const useMovements = (memberId: string, origin: api.OriginFilter) =>
  useInfiniteQuery({
    queryKey: ["portal", "member-movements", memberId, origin],
    queryFn: ({ pageParam }) =>
      api.listMovements(memberId, {
        origin,
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.meta.next ?? undefined,
    enabled: Boolean(memberId),
  });

export const useManualMemberRules = () =>
  useQuery({
    queryKey: ["portal", "point-rules", "member", "manual"],
    queryFn: api.listManualMemberRules,
  });

export const useAdjustmentReasons = () =>
  useQuery({
    queryKey: ["portal", "catalog-entries", "adjustment-reasons"],
    queryFn: api.listAdjustmentReasons,
  });

export function usePointMutations(memberId: string) {
  const client = useQueryClient();
  const invalidate = () => {
    void client.invalidateQueries({
      queryKey: ["portal", "member-movements", memberId],
    });
    // El saldo del miembro se muestra también en la ficha (MembersScreen.tsx)
    // y en el listado: sin invalidar ambos, se desactualiza tras registrar o
    // anular (mismo precedente que vehicleQueries.ts).
    void client.invalidateQueries({ queryKey: ["portal", "member", memberId] });
    void client.invalidateQueries({ queryKey: ["portal", "members"] });
  };
  return {
    register: useMutation({
      mutationFn: ({
        input,
        idempotencyKey,
      }: {
        input: api.RegisterPointsInput;
        idempotencyKey: string;
      }) => api.registerPointsAction(memberId, input, idempotencyKey),
      onSuccess: invalidate,
    }),
    void: useMutation({
      mutationFn: ({
        movementId,
        input,
        idempotencyKey,
      }: {
        movementId: string;
        input: api.VoidMovementInput;
        idempotencyKey: string;
      }) => api.voidMovement(movementId, input, idempotencyKey),
      onSuccess: invalidate,
    }),
  };
}
